export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; value: string }
  | { type: 'link'; href: string; value: string };

export type Block =
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'heading'; level: 1 | 2 | 3; children: InlineNode[] }
  | { type: 'rule' }
  | { type: 'list'; items: InlineNode[][] };

export interface MarkdownSection {
  id: string;
  heading: InlineNode[];
  headingText: string;
  blocks: Block[];
}

export interface MarkdownDoc {
  title: string;
  intro: Block[];
  sections: MarkdownSection[];
}

const inlinePattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;

export const parseInline = (text: string): InlineNode[] => {
  const nodes: InlineNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(inlinePattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      nodes.push({ type: 'text', value: text.slice(lastIndex, index) });
    }

    if (match[1] !== undefined) {
      nodes.push({ type: 'strong', value: match[1] });
    } else {
      nodes.push({ type: 'link', href: match[3], value: match[2] });
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return nodes.length > 0 ? nodes : [{ type: 'text', value: text }];
};

export const inlineText = (nodes: InlineNode[]): string =>
  nodes.map((node) => node.value).join('');

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/^\d+\.\s*/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const headingLevel = (line: string): 1 | 2 | 3 | undefined => {
  if (line.startsWith('### ')) {
    return 3;
  }
  if (line.startsWith('## ')) {
    return 2;
  }
  if (line.startsWith('# ')) {
    return 1;
  }
  return undefined;
};

const parseBlocks = (source: string): Block[] => {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (line.trim() === '') {
      index += 1;
      continue;
    }

    const level = headingLevel(line);
    if (level) {
      blocks.push({
        type: 'heading',
        level,
        children: parseInline(line.slice(level + 1)),
      });
      index += 1;
      continue;
    }

    if (/^-{3,}$/.test(line.trim())) {
      blocks.push({ type: 'rule' });
      index += 1;
      continue;
    }

    if (line.startsWith('- ')) {
      const items: InlineNode[][] = [];
      while (index < lines.length && lines[index].startsWith('- ')) {
        items.push(parseInline(lines[index].slice(2)));
        index += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    const paragraphLines: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() !== '' &&
      !headingLevel(lines[index]) &&
      !/^-{3,}$/.test(lines[index].trim()) &&
      !lines[index].startsWith('- ')
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }
    blocks.push({
      type: 'paragraph',
      children: parseInline(paragraphLines.join(' ')),
    });
  }

  return blocks;
};

export const parseMarkdownDoc = (source: string): MarkdownDoc => {
  const blocks = parseBlocks(source);
  const titleHeading = blocks.find(
    (block): block is Extract<Block, { type: 'heading' }> =>
      block.type === 'heading' && block.level === 1,
  );
  const title = titleHeading ? inlineText(titleHeading.children) : '';

  const body = blocks.filter((block) => block !== titleHeading);
  const intro: Block[] = [];
  const sections: MarkdownSection[] = [];
  const usedIds = new Set<string>();

  for (const block of body) {
    if (block.type === 'heading' && block.level === 3) {
      const headingText = inlineText(block.children);
      let id = slugify(headingText) || `section-${sections.length + 1}`;
      if (usedIds.has(id)) {
        id = `${id}-${sections.length + 1}`;
      }
      usedIds.add(id);
      sections.push({
        id,
        heading: block.children,
        headingText,
        blocks: [],
      });
      continue;
    }

    const currentSection = sections[sections.length - 1];
    if (currentSection) {
      currentSection.blocks.push(block);
    } else {
      intro.push(block);
    }
  }

  return { title, intro, sections };
};

export const firstBodyParagraph = (doc: MarkdownDoc): string => {
  const blocks = [
    ...doc.intro,
    ...doc.sections.flatMap((section) => section.blocks),
  ];
  const paragraph = blocks.find(
    (block): block is Extract<Block, { type: 'paragraph' }> => {
      if (block.type !== 'paragraph') {
        return false;
      }
      return !(
        block.children.length === 1 && block.children[0].type === 'strong'
      );
    },
  );

  return paragraph ? inlineText(paragraph.children) : '';
};
